import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi, ApiError } from '../../lib/api';
import {
  CUTDOWN_LENGTHS,
  type CutdownCreateResult,
  type CutdownInfo,
  type CutdownLength,
  type CutdownSuggestion,
} from '../../../shared/cutdown';
import { formatRuntimeLabel } from '../../../shared/runtime';
import Eyebrow from '../Eyebrow';
import Button from '../Button';
import CutdownReview from './CutdownReview';
import { SparkIcon, TickIcon } from './cutdownIcons';
import {
  failureCopy,
  lengthAvailable,
  lengthTileSub,
  retryLabel,
  reviewLabel,
  shortSeconds,
  suggestLabel,
} from './cutdownCopy';

interface Props {
  scriptId: string;
  /** Shown while the runtime loads, so the heading never flickers. */
  title: string;
  onClose: () => void;
  onCreated: (result: CutdownCreateResult) => void;
}

type Run =
  | { status: 'pending' }
  | { status: 'ready'; suggestion: CutdownSuggestion }
  | { status: 'failed'; message: string; aiFailure: boolean };

type Phase = 'choose' | 'working' | 'review';

/**
 * Make a cutdown (finishing bundle 2D, mock-up 08): choose lengths, wait for
 * one AI call per length, then review the struck-through cuts. Nothing is
 * written until "Create drafts", so Cancel, Escape and the scrim simply close.
 */
export default function CutdownDialog({ scriptId, title, onClose, onCreated }: Props) {
  const [info, setInfo] = useState<CutdownInfo | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CutdownLength[]>([]);
  const [phase, setPhase] = useState<Phase>('choose');
  const [runs, setRuns] = useState<Partial<Record<CutdownLength, Run>>>({});
  const [creating, setCreating] = useState(false);
  // Shared by every in-flight suggest call, aborted on close so a late
  // answer never lands in a dialog that's gone.
  const suggestAbort = useRef<AbortController | null>(null);
  useEffect(() => () => suggestAbort.current?.abort(), []);

  useEffect(() => {
    if (phase !== 'choose') return;
    const controller = new AbortController();
    setInfo(null);
    setInfoError(null);
    adminApi
      .getCutdownInfo(scriptId, controller.signal)
      .then((nextInfo) => {
        if (!controller.signal.aborted) setInfo(nextInfo);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setInfoError(err instanceof ApiError ? err.message : "Couldn't read this script. Try again.");
      });
    return () => controller.abort();
  }, [scriptId, phase]);

  const close = useCallback(() => {
    if (creating) return;
    suggestAbort.current?.abort();
    onClose();
  }, [creating, onClose]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  function suggest(lengths: CutdownLength[]) {
    if (!suggestAbort.current || suggestAbort.current.signal.aborted) {
      suggestAbort.current = new AbortController();
    }
    const signal = suggestAbort.current.signal;
    setRuns((prev) => {
      const next = { ...prev };
      for (const l of lengths) next[l] = { status: 'pending' };
      return next;
    });
    setPhase('working');
    // Separate calls in parallel: each length ticks off as it lands.
    for (const length of lengths) {
      adminApi
        .suggestCutdown(scriptId, length, signal)
        .then((suggestion) => {
          if (signal.aborted) return;
          setRuns((prev) => ({ ...prev, [length]: { status: 'ready', suggestion } }));
        })
        .catch((err) => {
          if (signal.aborted) return;
          const status = err instanceof ApiError ? err.status : 0;
          setRuns((prev) => ({
            ...prev,
            [length]: {
              status: 'failed',
              message: err instanceof ApiError ? err.message : `Workers AI didn't answer for the ${length}s cut.`,
              aiFailure: status === 0 || status === 502,
            },
          }));
        });
    }
  }

  const chosen = CUTDOWN_LENGTHS.filter((l) => selected.includes(l) &&
    (phase !== 'choose' || (info && lengthAvailable(info.estimate, l))));
  const ready = chosen.filter((l) => runs[l]?.status === 'ready');
  const failed = chosen.filter((l) => runs[l]?.status === 'failed');
  const pending = chosen.some((l) => runs[l]?.status === 'pending');

  // When the last length lands cleanly, grow straight into the review.
  useEffect(() => {
    if (phase === 'working' && !pending && failed.length === 0 && ready.length > 0) {
      setPhase('review');
    }
  }, [phase, pending, failed.length, ready.length]);

  function suggestAgain() {
    suggestAbort.current?.abort();
    setRuns({});
    setPhase('choose');
  }

  async function create(cuts: CutdownSuggestion[]) {
    const versions = new Set(cuts.map((c) => c.doc_version));
    if (versions.size !== 1) {
      throw new ApiError(409, 'This script changed after the cuts were suggested. Suggest them again.');
    }
    setCreating(true);
    try {
      const result = await adminApi.createCutdowns(
        scriptId,
        cuts[0].doc_version,
        cuts.map((c) => ({ seconds: c.seconds, keep: c.keep })),
      );
      onCreated(result);
    } finally {
      setCreating(false);
    }
  }

  if (phase === 'review' && info) {
    return (
      <CutdownReview
        info={info}
        suggestions={ready.map((l) => (runs[l] as { suggestion: CutdownSuggestion }).suggestion)}
        creating={creating}
        onCancel={close}
        onCreate={create}
        onSuggestAgain={suggestAgain}
      />
    );
  }

  const estimate = info?.estimate ?? null;
  const anyAvailable = CUTDOWN_LENGTHS.some((l) => lengthAvailable(estimate, l));
  const shownTitle = info?.title ?? title;

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-center justify-center px-6 z-50 animate-fade-in"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cutdown-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card border border-line rounded-md p-8 shadow-flare-lg/30 animate-scale-in"
      >
        <Eyebrow accent>// Make a cutdown</Eyebrow>
        <h2
          id="cutdown-title"
          className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2 break-words"
        >
          {phase === 'working' ? 'Trimming' : 'Cut down'} {shownTitle}
        </h2>

        {phase === 'choose' ? (
          <>
            <p className="text-muted text-[14px] leading-[1.55]">
              Pick one or more lengths. You'll see each cut before anything is created.
            </p>
            {infoError ? (
              <p className="text-error text-[13px] mt-4">{infoError}</p>
            ) : !info ? (
              <span className="inline-block mt-3.5 h-[34px] w-64 max-w-full rounded-sm bg-elevated skeleton" aria-hidden />
            ) : (
              <span className="inline-flex items-center gap-2 text-[13px] text-muted mt-3.5 px-3 py-2 rounded-sm bg-elevated">
                <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" aria-hidden />
                {estimate ? `Now: ${formatRuntimeLabel(estimate)}` : `Now: ~${info.words} words`}
              </span>
            )}

            {info && !anyAvailable ? (
              <p className="text-muted text-[14px] leading-[1.55] mt-6">
                {estimate
                  ? 'This script already runs 15 seconds or less, so there is nothing to cut.'
                  : 'This script has too little Audio to estimate a runtime yet, so there is nothing to cut. Add more Audio first.'}
              </p>
            ) : (
              !infoError && (
                <>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mt-[22px] mb-2">
                    Lengths
                  </span>
                  <div className="grid grid-cols-3 gap-2" role="group" aria-label="Lengths">
                    {CUTDOWN_LENGTHS.map((length) => (
                      <LengthTile
                        key={length}
                        length={length}
                        sub={info ? lengthTileSub(estimate, length) : ' '}
                        disabled={!info || !lengthAvailable(estimate, length)}
                        on={selected.includes(length)}
                        onToggle={() =>
                          setSelected((prev) =>
                            prev.includes(length) ? prev.filter((l) => l !== length) : [...prev, length],
                          )
                        }
                      />
                    ))}
                  </div>
                  <div className="flex gap-2.5 mt-4 px-3.5 py-3 rounded-sm border border-line bg-page text-[12.5px] leading-[1.5] text-muted">
                    <SparkIcon className="shrink-0 mt-0.5 text-faint" />
                    <span>
                      <b className="text-fg font-semibold">Uses Workers AI.</b> Included in your Cloudflare
                      plan, no extra cost. It only removes Audio lines and never rewrites them. Visual and
                      on-screen text stay as they are.
                    </span>
                  </div>
                </>
              )
            )}

            <div className="flex flex-col md:flex-row md:justify-end gap-3 mt-6">
              {info && anyAvailable ? (
                <>
                  <Button type="button" variant="ghost" className="w-full md:w-auto" onClick={close}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="w-full md:w-auto"
                    disabled={chosen.length === 0}
                    onClick={() => suggest(chosen)}
                  >
                    {suggestLabel(chosen.length)}
                  </Button>
                </>
              ) : (
                <Button type="button" variant="ghost" className="w-full md:w-auto" onClick={close}>
                  {info || infoError ? 'Close' : 'Cancel'}
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-muted text-[14px] leading-[1.55]">
              Usually under half a minute. Nothing has been created.
            </p>
            <div className="mt-5 border border-line rounded-sm overflow-hidden" aria-live="polite">
              {chosen.map((length, i) => (
                <ProgressRow key={length} length={length} run={runs[length]} first={i === 0} />
              ))}
            </div>

            {!pending && failed.length > 0 && (() => {
              const failures = failed.map((l) => runs[l] as Extract<Run, { status: 'failed' }>);
              const shared = failures.every((f) => !f.aiFailure && f.message === failures[0].message)
                ? failures[0].message
                : null;
              const copy = failureCopy(failed, ready, shared);
              return (
                <div className="mt-[18px] px-3.5 py-3 rounded-sm bg-error/[0.07] border border-error/25 text-[13px] text-muted leading-[1.5]">
                  <b className="block text-error font-semibold mb-0.5">{copy.lead}</b>
                  {copy.body}
                </div>
              );
            })()}

            <div className="flex flex-col md:flex-row md:justify-end gap-3 mt-6">
              <Button type="button" variant="ghost" className="w-full md:w-auto" onClick={close}>
                Cancel
              </Button>
              {!pending && failed.length > 0 && (
                <Button type="button" variant="secondary" className="w-full md:w-auto" onClick={() => suggest(failed)}>
                  {retryLabel(failed)}
                </Button>
              )}
              {(pending || ready.length > 0) && (
                <Button
                  type="button"
                  variant="primary"
                  className="w-full md:w-auto"
                  disabled={pending}
                  onClick={() => setPhase('review')}
                >
                  {reviewLabel(ready.length, failed.length > 0)}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LengthTile({
  length,
  sub,
  on,
  disabled,
  onToggle,
}: {
  length: CutdownLength;
  sub: string;
  on: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      aria-label={`${length} seconds, ${sub}`}
      disabled={disabled}
      onClick={onToggle}
      className={`
        relative block text-left px-3 pt-3 pb-[11px] rounded-sm border transition-colors duration-150
        focus:outline-none focus-visible:shadow-focus
        ${on
          ? 'border-accent bg-accent-glow-soft shadow-[inset_0_0_0_1px_var(--color-accent)]'
          : 'border-line bg-card hover:border-fg/40'}
        ${disabled ? 'opacity-50 cursor-not-allowed hover:border-line' : ''}
      `}
    >
      <span
        className={`flex mb-2 md:mb-0 md:absolute md:top-2.5 md:right-2.5 w-4 h-4 rounded-[4px] border-[1.5px] items-center justify-center ${
          on ? 'bg-accent border-accent text-accent-fg' : 'border-faint/60 bg-card'
        }`}
        aria-hidden
      >
        {on && <TickIcon />}
      </span>
      <span className="block font-display font-semibold text-[24px] leading-none text-fg">{length}s</span>
      <span className={`block text-[11.5px] mt-1.5 ${disabled ? 'text-muted' : 'text-faint'}`}>{sub}</span>
    </button>
  );
}

function ProgressRow({ length, run, first }: { length: CutdownLength; run?: Run; first: boolean }) {
  const status = run?.status ?? 'pending';
  return (
    <div className={`flex items-center gap-3 px-3.5 py-3 text-[14px] ${first ? '' : 'border-t border-line-soft'}`}>
      {status === 'ready' ? (
        <span className="w-[18px] h-[18px] rounded-full bg-success/15 text-success inline-flex items-center justify-center shrink-0" aria-hidden>
          <TickIcon />
        </span>
      ) : status === 'failed' ? (
        <span className="w-[18px] h-[18px] rounded-full border-2 border-dashed border-faint/50 shrink-0" aria-hidden />
      ) : (
        <span className="w-[18px] h-[18px] rounded-full border-2 border-line border-t-muted animate-spin shrink-0" aria-hidden />
      )}
      <span className="flex-1 font-semibold text-fg">{length} seconds</span>
      <span
        className={`font-mono text-[10.5px] tracking-[0.1em] uppercase ${
          status === 'ready' ? 'text-success' : status === 'failed' ? 'text-error' : 'text-faint'
        }`}
      >
        {run?.status === 'ready'
          ? `Ready · ${shortSeconds(run.suggestion.runtime_seconds)}`
          : status === 'failed'
            ? "Didn't finish"
            : 'Trimming…'}
      </span>
    </div>
  );
}
